import React from 'react'
import { isNull } from 'lodash'
import {
  TestSessionsWithoutMembers_testSessions_pagination,
  SortDirectionEnum,
  getFormattedDateAndTime,
  TestType,
  useGenerateNewInvoice,
  TestSessionsWithoutMembers_testSessions_testSessions,
} from '@modmd/data'
import { TestSessionStatusLabel } from 'sharedComponents/TestSessionStatusLabel'
import { TestSessionPaymentLabel } from 'sharedComponents/TestSessionPaymentLabel'
import { Box } from 'components/Layout'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE } from 'theme'
import Label from 'components/Label'
import { Checkbox } from 'components/Checkbox'
import Button from 'components/Button'
import { ConfirmationDialog } from 'components/Dialog'
import { Text } from 'components/Typography'
import { isInfluenza } from 'utils/helpers'

interface ColumnLabels {
  date?: string | null
  time?: string | null
  location?: string | null
  numOfPeople?: string | null
  company?: string | null
  group?: string | null
  status?: string | null
  paymentStatus?: string | null
  invoiceCheck?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  testSessions: TestSessionsWithoutMembers_testSessions_testSessions[]
  sorter?: DataListProps['sorter']
  columnLabels?: ColumnLabels
  pagination?: TestSessionsWithoutMembers_testSessions_pagination
  isCard?: boolean
  isLoading?: boolean
  hasPaginationInFooter?: boolean
  onPageChange?: (page: number) => void
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  pageLength?: number
  listOfInvoices?: Array<string>
  setListOfInvoices?: React.Dispatch<React.SetStateAction<Array<string>>>
  testSessionRefetch?: () => void
  onRowClick: (data: TestSessionsWithoutMembers_testSessions_testSessions) => void
}

export const PAGE_LENGTH = 10

const defaultColumnLabels = {
  id: 'Session Id',
  date: 'Date',
  time: 'Time',
  name: 'Name',
  location: 'Location',
  numOfPeople: 'Number of people',
  company: 'Company',
  group: 'Group',
  status: 'Status',
  paymentStatus: 'Payment',
  invoiceCheck: 'Invoice',
}

export const TestSessionsDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  testSessions,
  sorter,
  pagination,
  columnLabels: providedColumnLabels,
  isCard = true,
  isLoading,
  hasPaginationInFooter = false,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
  listOfInvoices,
  setListOfInvoices,
  testSessionRefetch,
}) => {
  const [confirmModal, setConfirmModal] = React.useState(false)
  const [invoiceData, setInvoiceData] = React.useState({
    invoiceId: '',
    testSessionId: '',
  })

  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const isTablet = useIsMinDevice(DEVICE.TABLET)
  const [newInvoice, { loading: isLoadingNewInvoice }] = useGenerateNewInvoice()
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])
  const paginationComp = pagination && pagination?.total > pageLength && onPageChange && (
    <Pagination
      firstPage={1}
      lastPage={Math.ceil(pagination.total / pageLength) || 1}
      currentPage={Math.ceil(pagination.from / pageLength) + 1}
      onPageChange={onPageChange}
    />
  )
  const addOrRemoveInvoice = (id: string) => {
    if (setListOfInvoices && listOfInvoices)
      if (listOfInvoices.includes(id))
        setListOfInvoices(listOfInvoices.filter((item) => item !== id))
      else setListOfInvoices([...listOfInvoices, id])
  }

  const showConfirmGenerateInvoice = (invoiceId = '') => {
    const testSessionsIds = testSessions
      .filter((ts) => ts.Invoice?.invoiceId === invoiceId)
      .map((ts) => ts.id)
      .join(',')
    setInvoiceData({
      invoiceId,
      testSessionId: testSessionsIds,
    })
    setConfirmModal(true)
  }

  const dismissConfirmGenerateInvoice = () => {
    setConfirmModal(false)
    setInvoiceData({
      invoiceId: '',
      testSessionId: '',
    })
  }

  const generateOtherInvoice = async (id: string) => {
    await newInvoice({
      variables: {
        inputData: id,
      },
    })
    setConfirmModal(false)
    setInvoiceData({
      invoiceId: '',
      testSessionId: '',
    })
    if (testSessionRefetch) {
      void testSessionRefetch()
    }
  }

  const Wrapper = isCard ? Card : Box

  return (
    <>
      <Wrapper>
        <Datalist
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={testSessions}
          sorter={sorter}
          onSortChange={onSortChange}
          header={tableHeader}
          footer={hasPaginationInFooter && paginationComp ? paginationComp : tableFooter}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={
            isDesktop
              ? () => (
                  <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
                    {!isNull(columnLabels.id) ? (
                      <Datalist.HeaderCell label={columnLabels.id} />
                    ) : null}
                    {!isNull(columnLabels.invoiceCheck) ? (
                      <Datalist.HeaderCell label={columnLabels.invoiceCheck} sortKey="invoice" />
                    ) : null}
                    {!isNull(columnLabels.date) ? (
                      <Datalist.HeaderCell label={columnLabels.date} sortKey="date" />
                    ) : null}
                    {!isNull(columnLabels.time) ? (
                      <Datalist.HeaderCell label={columnLabels.time} />
                    ) : null}
                    {!isNull(columnLabels.name) ? (
                      <Datalist.HeaderCell label={columnLabels.name} />
                    ) : null}
                    {!isNull(columnLabels.location) ? (
                      <Datalist.HeaderCell label={columnLabels.location} />
                    ) : null}
                    {!isNull(columnLabels.numOfPeople) ? (
                      <Datalist.HeaderCell label={columnLabels.numOfPeople} />
                    ) : null}
                    {!isNull(columnLabels.company) ? (
                      <Datalist.HeaderCell label={columnLabels.company} />
                    ) : null}
                    {!isNull(columnLabels.group) ? (
                      <Datalist.HeaderCell label={columnLabels.group} />
                    ) : null}
                    {!isNull(columnLabels.status) ? (
                      <Datalist.HeaderCell label={columnLabels.status} />
                    ) : null}
                    {!isNull(columnLabels.paymentStatus) ? (
                      <Datalist.HeaderCell label={columnLabels.paymentStatus} />
                    ) : null}
                  </Datalist.Row>
                )
              : undefined
          }
          renderTableRow={(data) => {
            const { id, date, name, Location, paymentStatus, type, Invoice, finishedAt } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                {...(!isNull(columnLabels.invoiceCheck) ? {} : { onClick: () => onRowClick(data) })}
                {...(!isTablet ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.id) ? (
                  <Datalist.Cell label={columnLabels.id}>{id}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.invoiceCheck) ? (
                  <Datalist.Cell label={columnLabels.invoiceCheck}>
                    {Invoice?.invoiceId || !finishedAt ? (
                      <>
                        {Invoice?.invoiceId && (
                          <Button
                            appearance="ghost"
                            size="small"
                            onClick={() => showConfirmGenerateInvoice(Invoice?.invoiceId as string)}
                            style={{ whiteSpace: 'initial', height: '100%' }}
                          >
                            {`Invoice ID: ${Invoice?.invoiceId ?? ''}`}
                          </Button>
                        )}
                        {!Invoice?.invoiceId && (
                          <Label size="l" indicator="grayDark">
                            Not finished yet
                          </Label>
                        )}
                      </>
                    ) : (
                      <Checkbox
                        checked={listOfInvoices && listOfInvoices.includes(id)}
                        onChange={() => addOrRemoveInvoice(id)}
                        label=""
                        hasBottomContent={false}
                      />
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.date) ? (
                  <Datalist.Cell label={columnLabels.date}>
                    {getFormattedDateAndTime(date)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.time) ? (
                  <Datalist.Cell label={columnLabels.time}>
                    {getFormattedDateAndTime(date, true, false)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>
                    {type === TestType.SofiaRsv && <Label indicator="grayDark">RSV</Label>}
                    {isInfluenza(type) && <Label indicator="grayDark">Influenza</Label>}
                    {type === TestType.RapidCovid19 && <Label indicator="grayDark">Rapid</Label>}
                    {type === TestType.Covid19 && <Label indicator="grayDark">PCR</Label>}
                    {(type === TestType.Antigen ||
                      type === TestType.AntigenCareStart ||
                      type === TestType.SiennaAntigen ||
                      type === TestType.IndicaidAntigen) && (
                      <Label indicator="grayDark">Antigen</Label>
                    )}
                    {type === TestType.Lucira && <Label indicator="grayDark">Lucira</Label>}
                    {name ?? 'unnamed'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.location) ? (
                  <Datalist.Cell label={columnLabels.location}>
                    {Location.street}, {Location.city}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.company) ? (
                  <Datalist.Cell label={columnLabels.company}>
                    {data?.Company?.name || ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.group) ? (
                  <Datalist.Cell label={columnLabels.group}>
                    {data?.Group?.name}
                    {(!data?.Group && data?.Groups?.map(({ name }) => name).join(', ')) || ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.status) ? (
                  <Datalist.Cell label={columnLabels.status}>
                    <TestSessionStatusLabel session={data} />
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.paymentStatus) ? (
                  <Datalist.Cell label={columnLabels.paymentStatus}>
                    <TestSessionPaymentLabel
                      paymentStatus={paymentStatus}
                      invoices={Invoice?.invoiceDocNumber ?? Invoice?.invoiceId}
                      isInvoiceDocNumber={!!Invoice?.invoiceDocNumber}
                    />
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
        />
      </Wrapper>
      {!hasPaginationInFooter && paginationComp && (
        <Pagination.Wrapper>{paginationComp}</Pagination.Wrapper>
      )}
      <ConfirmationDialog
        title="Generate invoice"
        isOpen={confirmModal}
        onDismiss={dismissConfirmGenerateInvoice}
        actions={
          <>
            <Button onClick={dismissConfirmGenerateInvoice} colorVariant="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => generateOtherInvoice(invoiceData.invoiceId)}
              isFetching={isLoadingNewInvoice}
            >
              Cancel and Generate
            </Button>
          </>
        }
      >
        <>
          <Text>Do you want to cancel this invoice and generate a new one? </Text>
          {invoiceData.testSessionId && invoiceData.invoiceId && (
            <Text>
              <strong>Session:</strong> {invoiceData.testSessionId} <strong>InvoiceId:</strong>{' '}
              {invoiceData.invoiceId}
            </Text>
          )}
        </>
      </ConfirmationDialog>
    </>
  )
}
